import React, { useEffect, useMemo } from 'react'

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { useSelector } from 'react-redux'

import { DatePicker, DummyInput, Input, NumberInput, PriceInput } from '@/components/Form'
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Spacer,
} from '@chakra-ui/react'

import { InputCell, NumberInputCell, PriceCell, SelectCell } from '@/components/Table/components/Cell'
import EditableTable from '@/components/Table/EditableTable'
import { Currency } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { sumArray } from '@/utils/arrayHelper'
import { numberWithCommas, roundNumber } from '@/utils/stringHelper'
import { createColumnHelper } from '@tanstack/react-table'

const MaintenanceTotalContractPriceSection = ({ subSystemIndex }) => {
  const offerPermissions = useSelector(selectOfferPermissions)
  const { adminMode } = useSelector(selectOfferDetail)

  const { setValue, getValues } = useFormContext()
  const columnHelper = createColumnHelper()

  const newContractRows = useWatch({ name: `subSystems.${subSystemIndex}.totalContractPrice.newContractRows` })

  const totalContractPrice = useWatch({ name: `subSystems.${subSystemIndex}.totalContractPrice.currentContract.total` })
  const exchangeRate = useWatch({ name: `exchangeRate` })
  const currency = useWatch({ name: `currency` })
  const newOrRenewal = useWatch({ name: `subSystems.${subSystemIndex}.newOrRenewal` })
  const { append, remove } = useFieldArray({
    name: `subSystems.${subSystemIndex}.totalContractPrice.newContractRows`,
  })
  const currentLastMonthPrice = useWatch({
    name: `subSystems.${subSystemIndex}.totalContractPrice.currentContract.lastMonthPrice`,
  })

  //Translated Variables
  const canEditSubSystem = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_SUB_SYSTEM]?.edit ?? false
  const currentTotalContractPriceHKD = useMemo(() => {
    if (currency === Currency.HKD) return 0

    const total = parseFloat(totalContractPrice) || 0
    const exchangeRateValue = parseFloat(exchangeRate) || 1

    return total * exchangeRateValue || 0
  }, [currency, totalContractPrice, exchangeRate])

  useEffect(() => {
    if (exchangeRate) {
      //Adjust the totalHKD
      const currentNewContractRows = getValues(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows`)
      currentNewContractRows.forEach((row, i) => {
        const totalPrice = row.total || 0
        setValue(
          `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${i}.totalHKD`,
          totalPrice * exchangeRate,
          {
            shouldDirty: true,
          }
        )
      })
    }
  }, [subSystemIndex, exchangeRate, setValue, getValues])

  useEffect(() => {
    if (currentLastMonthPrice && currentLastMonthPrice > 0) {
      // Update First Row's Average Increment
      const firstRowMonthlyPrice = getValues(
        `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.0.monthlyPrice`
      )

      if (firstRowMonthlyPrice && firstRowMonthlyPrice > 0) {
        const averageIncrement = roundNumber(
          parseFloat((firstRowMonthlyPrice - currentLastMonthPrice) / currentLastMonthPrice) * 100 || 0,
          2
        )
        setValue(
          `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.0.averageIncrement`,
          averageIncrement,
          {
            shouldDirty: true,
          }
        )
      }
    }
  }, [subSystemIndex, currentLastMonthPrice, getValues, setValue])

  //Handler
  const handleAddContractPrice = () => {
    append({
      forYear: 1,
      duration: 0,
      monthlyPrice: 0,
      yearlyPrice: 0,
      durationUnit: 'monthly',
      total: 0,
      totalHKD: 0,
      averageIncrement: 0,
    })
  }

  const updateMyData = (rowIndex, columnId, value) => {
    if (['duration', 'total'].includes(columnId)) {
      const numericValue = parseFloat(value) || 0
      setValue(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.${columnId}`, numericValue)
    } else {
      setValue(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.${columnId}`, value)
    }

    if (['duration', 'durationUnit', 'total'].includes(columnId)) {
      //Need Modify Other
      const duration =
        columnId === 'duration'
          ? value
          : getValues(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.duration`)
      const totalPrice =
        columnId === 'total'
          ? value
          : getValues(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.total`)
      const durationUnit =
        columnId === 'durationUnit'
          ? value
          : getValues(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.durationUnit`)

      if (!totalPrice || !duration || !durationUnit || duration < 1) {
        return
      }

      const perunitRate = totalPrice / duration
      const monthlyPrice = durationUnit === 'monthly' ? perunitRate : perunitRate / 12
      const yearlyPrice = durationUnit === 'yearly' ? perunitRate : perunitRate * 12

      setValue(
        `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.monthlyPrice`,
        roundNumber(monthlyPrice, 2)
      )
      setValue(
        `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.yearlyPrice`,
        roundNumber(yearlyPrice, 2)
      )
      setValue(
        `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.totalHKD`,
        totalPrice * exchangeRate,
        {
          shouldDirty: true,
        }
      )

      // Update Average Increment
      const contractType = getValues(`subSystems.${subSystemIndex}.newOrRenewal`)

      let lastMonthPrice
      if (rowIndex === 0) {
        // Get Current Contract Last Month Contract Price

        lastMonthPrice =
          contractType === 'new'
            ? 0
            : getValues(`subSystems.${subSystemIndex}.totalContractPrice.currentContract.lastMonthPrice`)
      } else {
        // Get Last Year Price
        lastMonthPrice = newContractRows[rowIndex - 1]?.monthlyPrice
      }

      if (!isNaN(parseInt(lastMonthPrice)) && lastMonthPrice > 0) {
        const newIncrement = roundNumber(((parseFloat(monthlyPrice) - lastMonthPrice) / lastMonthPrice) * 100, 2) || 0

        setValue(
          `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.averageIncrement`,
          newIncrement,
          {
            shouldDirty: true,
          }
        )
      } else {
        setValue(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex}.averageIncrement`, 0, {
          shouldDirty: true,
        })
      }

      const nextRow = getValues(`subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex + 1}`)
      if (nextRow && monthlyPrice > 0) {
        const nextRowNewIncreament =
          roundNumber(((parseFloat(nextRow?.monthlyPrice) - monthlyPrice) / monthlyPrice) * 100, 2) || 0
        setValue(
          `subSystems.${subSystemIndex}.totalContractPrice.newContractRows.${rowIndex + 1}.averageIncrement`,
          nextRowNewIncreament,
          {
            shouldDirty: true,
          }
        )
      }
    }
  }
  const hiddenColumns = useMemo(() => {
    const hidden = []

    if (currency === 'HKD') {
      hidden.push('totalHKD')
    }

    return hidden
  }, [currency])
  const columnDef = useMemo(
    () => [
      columnHelper.accessor('forYear', {
        header: () => <>Year</>,
        cell: ({ getValue }) => {
          const forYear = getValue()

          if (forYear > -1) {
            return <>{forYear}</>
          }
          return <></>
        },
        size: 50,
      }),
      columnHelper.accessor('description', {
        cell: InputCell,
        footer: 'Total',
      }),
      columnHelper.accessor('duration', {
        cell: NumberInputCell,
      }),
      columnHelper.accessor('durationUnit', {
        header: () => <>Unit</>,
        cell: (cell) => (
          <SelectCell
            {...cell}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
        ),
      }),
      columnHelper.accessor('yearlyPrice', {
        size: 130,
        cell: PriceCell,
      }),
      columnHelper.accessor('monthlyPrice', {
        size: 130,
        cell: PriceCell,
      }),
      columnHelper.accessor('total', {
        header: () => <>{`Total${currency !== 'HKD' ? ` (${currency})` : ''}`}</>,
        size: 200,

        cell: (cell) => {
          return (
            <InputGroup alignItems={'center'}>
              <InputLeftAddon>$</InputLeftAddon>
              <NumberInputCell {...cell} />
            </InputGroup>
          )
        },
      }),
      columnHelper.accessor('totalHKD', {
        header: () => <>Total (HKD)</>,
        cell: PriceCell,
        footer: ({ table }) => {
          const allTotal = roundNumber(
            sumArray(table.getCoreRowModel().flatRows.map((row) => row.getValue('totalHKD'))),
            2
          )

          return <span>{`$${numberWithCommas(allTotal)}`}</span>
        },
      }),
      columnHelper.accessor('averageIncrement', {
        header: () => <>Total (%)</>,
        cell: ({ getValue, row: { original } }) => {
          const averageIncrement = getValue()
          if (original.forYear > -1) {
            return <span>{averageIncrement}%</span>
          }
          return <></>
        },
        footer: ({ table }) => {
          const allTotal = sumArray(table.getCoreRowModel().flatRows.map((row) => row.getValue('totalHKD')))
          const allMonth = sumArray(
            table.getCoreRowModel().flatRows.map((row) => {
              const durationUnit = row.getValue('durationUnit')
              const duration = row.getValue('duration')
              if (durationUnit === 'monthly') {
                return duration
              } else {
                return duration * 12
              }
            })
          )
          const oldAverageMonthlyPrice = parseInt(
            getValues(`subSystems.${subSystemIndex}.totalContractPrice.currentContract.lastMonthPrice`)
          )
          if (oldAverageMonthlyPrice === 0 || newOrRenewal === 'new') {
            return <span></span>
          }

          const average = parseFloat(
            ((allTotal / allMonth - oldAverageMonthlyPrice) / oldAverageMonthlyPrice) * 100
          ).toFixed(2)

          return <span>{`${isNaN(average) ? 0 : average}%`}</span>
        },
      }),

      columnHelper.display({
        id: 'action',
        cell: ({ row: { index }, table }) => {
          const disabled = table.options.meta?.disabled
          if (disabled) {
            return <></>
          }

          return (
            <Button colorScheme="red" onClick={() => remove(index)} disabled={disabled}>
              Delete
            </Button>
          )
        },
      }),
    ],
    [columnHelper, currency, getValues, subSystemIndex, newOrRenewal, remove]
  )

  return (
    <Flex gap="5" direction={'column'}>
      {newOrRenewal !== 'new' && (
        <Box mb="5">
          <Heading size="md" mb="1">
            Current Contract
          </Heading>

          <Grid templateColumns="repeat(12, 1fr)" gap={4} mb="3">
            <GridItem colSpan="6">
              <DatePicker
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.startDate`}
                label="Contract Start Date"
                disabled={!canEditSubSystem}
                isRow
                labelWidth={50}
              />
            </GridItem>
            <GridItem colSpan="6">
              <DatePicker
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.endDate`}
                label="Contract End Date"
                disabled={!canEditSubSystem}
                isRow
                labelWidth={50}
              />
            </GridItem>

            <GridItem colSpan="6">
              <PriceInput
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.total`}
                label={`Total${currency && currency !== 'HKD' ? ` (${currency})` : ''}`}
                placeholder="Enter value"
                disabled={!canEditSubSystem}
                leadingIcon={<InputLeftAddon>$</InputLeftAddon>}
                isRow
              />
            </GridItem>
            {currency !== 'HKD' && (
              <GridItem colSpan="6">
                <DummyInput
                  name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.totalHKD`}
                  label="Total (HKD)"
                  value={numberWithCommas(currentTotalContractPriceHKD)}
                  leadingIcon={<InputLeftAddon>$</InputLeftAddon>}
                  isRow
                />
              </GridItem>
            )}
          </Grid>
          <Grid templateColumns="repeat(12, 1fr)" gap={4}>
            <GridItem colSpan="5">
              <PriceInput
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.lastMonthPrice`}
                label="Last Month Price"
                tooltip={'Contract Price of Last Month'}
                placeholder="Enter value"
                disabled={!canEditSubSystem}
                leadingIcon={<InputLeftAddon>$</InputLeftAddon>}
                isRow
                labelWidth={40}
              />
            </GridItem>
            <GridItem colSpan="3">
              <NumberInput
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.grossMargin`}
                label="Gross Margin "
                type="number"
                placeholder="Enter value"
                disabled={!canEditSubSystem}
                trailingIcon={<InputRightAddon>%</InputRightAddon>}
                isRow
                labelWidth={50}
              />
            </GridItem>
            <GridItem colSpan="4">
              <NumberInput
                name={`subSystems.${subSystemIndex}.totalContractPrice.currentContract.averageIncrement`}
                label="Average Increment"
                placeholder="Enter value"
                disabled={!canEditSubSystem}
                trailingIcon={<InputRightAddon>%</InputRightAddon>}
                isRow
                labelWidth={50}
              />
            </GridItem>
          </Grid>
        </Box>
      )}
      <Box mb="5">
        <Flex mb="2">
          <Heading size="md" mb="1">
            {newOrRenewal === 'new' ? 'New Contract' : 'Renewal Contract'}
          </Heading>
          <Spacer />
          {canEditSubSystem && (
            <Button colorScheme="green" onClick={handleAddContractPrice}>
              Add
            </Button>
          )}
        </Flex>
        <Box overflowX={'scroll'}>
          <EditableTable
            columnDef={columnDef}
            data={newContractRows}
            disabled={!adminMode}
            showFooter
            updateData={updateMyData}
            hiddenColumns={hiddenColumns}
            tableWidth="100%"
          />
        </Box>
      </Box>
    </Flex>
  )
}

export default MaintenanceTotalContractPriceSection
